/**
 * Every /budget read, each under its own run key through D1's useKeyedRead, `budget|<kind>|<ws>|a:<attempt>`:
 * - config and readiness, which a config write changes: refreshConfig() re-reads these two after box S's confirm or
 *   box 1's selector;
 * - active, accounts, facts (D1's '28d' pair: R17, R25) and mapping (D1's loadMapping).
 * retry() re-reads all six once. A null scope, or the master's all-workspaces view (R23: every box needsWorkspace),
 * reads nothing: every key is ''.
 *
 * @module hooks/useBudgetData
 */
import { useCallback, useMemo, useState } from 'react';
import { readActiveVersion, readAdAccounts, readConfig, readReadiness, type ActiveVersion, type MmmConfig } from '@/services/budget/budgetReaders';
import type { ReadinessRow } from '@/services/budget/readinessRules';
import type { AdAccount } from '@/services/budget/scopeRows';
import { bangkokToday } from '@/services/looker/lookerDateRange';
import { loadFactPair, loadMapping } from '@/services/overview/overviewLoaders';
import type { FactPair, OverviewWindows, ReadState } from '@/services/overview/overviewTypes';
import { overviewWindowsOf } from '@/services/overview/overviewWindows';
import type { ResultMappingInfo } from '@/services/targets/resultMetric';
import type { OverviewScope } from './useOverviewData';
import { useKeyedRead } from './useKeyedRead';

export interface BudgetData {
  /** D1's '28d' windows (R17). Box I's CPR reads cut and cutPrevious, which sit inside them (T10j). */
  windows: OverviewWindows | null;
  /** ready null = nothing confirmed yet (R3). */
  config: ReadState<MmmConfig | null>;
  /** ready null = no version yet. A stored bundle that fails parseBundle is failed. */
  active: ReadState<ActiveVersion | null>;
  /** ready null = a workspace the caller cannot see. */
  readiness: ReadState<ReadinessRow | null>;
  accounts: ReadState<AdAccount[]>;
  facts: ReadState<FactPair>;
  /** loadMapping never rejects: a failed mapping read is ready with FAILED_MAPPING (data.failed), never failed. */
  mapping: ReadState<ResultMappingInfo>;
  /** Re-reads all six once. */
  retry: () => void;
  /** After a config write: re-reads the config and the readiness only. */
  refreshConfig: () => void;
}

const unscoped = (): Promise<never> => Promise.reject(new Error('budget: no workspace'));

export const useBudgetData = (scope: OverviewScope | null): BudgetData => {
  const [attempt, setAttempt] = useState(0);
  const [configAttempt, setConfigAttempt] = useState(0);
  // retry() moves both counters, so it re-reads the config and the readiness too.
  const retry = useCallback(() => { setAttempt((n) => n + 1); setConfigAttempt((n) => n + 1); }, []);
  const refreshConfig = useCallback(() => setConfigAttempt((n) => n + 1), []);
  const windows = useMemo(() => overviewWindowsOf('28d', bangkokToday()), []);
  // workspaceId null is the master's all-workspaces view (R23).
  const ws = scope?.workspaceId ?? null;
  const keyOf = (kind: string, n: number): string => (ws ? `budget|${kind}|${ws}|a:${n}` : '');

  const config = useKeyedRead<MmmConfig | null>(keyOf('config', configAttempt), () => (ws ? readConfig(ws) : unscoped()), 'BudgetConfig');
  const readiness = useKeyedRead<ReadinessRow | null>(keyOf('readiness', configAttempt),
    () => (ws ? readReadiness(ws) : unscoped()), 'BudgetReadiness');
  const active = useKeyedRead<ActiveVersion | null>(keyOf('active', attempt), () => (ws ? readActiveVersion(ws) : unscoped()), 'BudgetActive');
  const accounts = useKeyedRead<AdAccount[]>(keyOf('accounts', attempt), () => (ws ? readAdAccounts(ws) : unscoped()), 'BudgetAccounts');
  const facts = useKeyedRead<FactPair>(windows ? keyOf('facts', attempt) : '',
    () => (ws && windows ? loadFactPair([ws], windows) : unscoped()), 'BudgetFacts');
  const mapping = useKeyedRead<ResultMappingInfo>(keyOf('mapping', attempt), () => (ws ? loadMapping(ws) : unscoped()), 'BudgetMapping');

  return useMemo(
    () => ({ windows, config, active, readiness, accounts, facts, mapping, retry, refreshConfig }),
    [windows, config, active, readiness, accounts, facts, mapping, retry, refreshConfig],
  );
};
